import React from 'react';
import { ArrowLeft } from 'lucide-react';
import Seo from './components/Seo';

const sections = [
  {
    title: '1. Acceptance of Terms',
    paragraphs: [
      'These Terms and Conditions govern your access to and use of the Jobisa website, Jobisa Worker app, Jobisa Recruiter app, public worker profiles, messaging features, recruiter dashboards, WhatsApp onboarding flows, and related services operated under the Jobisa brand.',
      'By registering, accessing, browsing, uploading content, or using any part of the Platform, you agree to be bound by these Terms, our Privacy Policy, and any additional operational rules or product-specific notices we make available to you.',
    ],
  },
  {
    title: '2. Eligibility and Authority',
    bullets: [
      'Workers must use the Platform only if they are legally capable of entering into a binding arrangement under applicable law.',
      'Recruiters, agencies, and business users must have the authority to act on behalf of the organization they represent and must provide accurate business and contact details.',
      'Jobisa may suspend, restrict, or refuse access where eligibility, authority, identity, or lawful use is reasonably in doubt.',
    ],
  },
  {
    title: '3. Platform Services',
    paragraphs: [
      'Jobisa is a hiring and workforce-connect platform. It helps workers create profiles, discover jobs, share credentials, receive notifications, and communicate with recruiters. It helps recruiters publish campaigns and jobs, review worker profiles, manage hiring workflows, communicate with applicants, and use token-based or other paid platform features where applicable.',
      'Jobisa may also offer onboarding, support, or lead-intake channels through WhatsApp, AI-assisted interfaces, or similar communication tools. These tools are provided to support recruitment operations and are subject to these Terms and our Privacy Policy.',
    ],
  },
  {
    title: '4. Account Registration and Security',
    bullets: [
      'You must provide accurate, current, and complete information while creating or maintaining an account.',
      'You are responsible for safeguarding your login credentials, device access, and any activity occurring under your account.',
      'You must notify Jobisa promptly if you believe your account, phone number, or authentication credentials have been compromised.',
      'Jobisa may require additional verification before allowing access to sensitive features, payouts, hiring tools, or profile recovery.',
    ],
  },
  {
    title: '5. Worker Responsibilities',
    bullets: [
      'Workers must provide truthful profile information, qualifications, skills, work history, documents, and application responses.',
      'Workers must not impersonate another person, submit forged or misleading documents, or share unlawful, abusive, or infringing content.',
      'Workers remain responsible for verifying job terms, salary, location, recruiter details, and employment conditions before accepting or attending any opportunity.',
      'Use of public profiles, voice CV, video CV, chat, or WhatsApp onboarding must remain lawful and respectful at all times.',
      'By recording and submitting a Video CV or Voice CV, you consent to Jobisa storing, processing, and sharing that recording with verified recruiters for the purpose of job matching.',
    ],
  },
  {
    title: '6. Recruiter Responsibilities',
    bullets: [
      'Recruiters must post lawful, genuine, non-misleading jobs and campaigns and must ensure their hiring process complies with applicable labour, immigration, anti-discrimination, and industry requirements.',
      'Recruiters must not request unlawful payments, exploitative work conditions, forged documentation, or personal data beyond what is reasonably necessary for hiring and onboarding.',
      'Recruiters are responsible for the accuracy of hiring content, interview schedules, compensation details, company branding, and communications sent through the Platform.',
      'Recruiters must use worker information only for legitimate recruitment and employment-related purposes consistent with applicable law and platform rules.',
    ],
  },
  {
    title: '7. Content, Uploads, and Communications',
    paragraphs: [
      'You retain responsibility for content, messages, documents, media, and other materials you upload, send, or publish through Jobisa. By submitting content, you grant Jobisa a non-exclusive, revocable, limited license to host, store, process, display, reproduce, and use that content as necessary to operate, secure, improve, and administer the Platform.',
      'Jobisa may review, moderate, restrict, remove, or preserve content where reasonably necessary for platform integrity, user safety, legal compliance, dispute resolution, fraud prevention, or enforcement of these Terms.',
    ],
  },
  {
    title: '8. AI-Assisted Features and WhatsApp Flows',
    paragraphs: [
      'Jobisa may use AI-assisted tools for worker onboarding, profile extraction, voice or text assistance, or response drafting. These features are intended to support user experience and operational efficiency, but they may occasionally produce incomplete, inaccurate, or unsuitable output.',
      'Users must independently review important hiring, identity, document, travel, salary, and legal information before relying on any AI-assisted output. Jobisa may also use WhatsApp or similar messaging channels for structured onboarding, support, reminders, or follow-up messages, subject to applicable law and platform permissions.',
      'By messaging the Jobisa WhatsApp bot, you explicitly opt-in to receive job alerts, onboarding messages, and platform updates via WhatsApp. You may opt-out of all WhatsApp messages at any time by typing "STOP" in the chat. Opting out will stop all automated messages but will not delete your account or previously submitted data.',
    ],
  },
  {
    title: '9. Fees, Tokens, and Billing',
    paragraphs: [
      'Certain recruiter-side features may be subject to token balances, pricing rules, wallet deductions, promotional credits, free onboarding balances, or other billing controls published in-product or communicated by Jobisa. Recruiters are responsible for reviewing the applicable pricing before using paid features.',
      'Jobisa may modify token pricing, promotional rules, signup credits, plan structures, or billing logic from time to time. Misuse, abuse, duplicate account creation for incentives, or billing fraud may lead to suspension, reversal of credits, or termination.',
    ],
  },
  {
    title: '10. Prohibited Conduct',
    bullets: [
      'Impersonation, identity fraud, misleading job posts, forged documents, or manipulation of platform trust signals.',
      'Scraping, bulk extraction, automated abuse, unauthorized reverse engineering, or attempts to bypass technical or commercial restrictions.',
      'Spam, harassment, discrimination, threats, hateful conduct, or unlawful solicitation on the Platform.',
      'Uploading malware, malicious files, harmful scripts, or content that infringes intellectual property, privacy, or publicity rights.',
      'Use of the Platform for illegal recruitment, trafficking, deceptive migration practices, or any unlawful commercial activity.',
    ],
  },
  {
    title: '11. Suspension, Restriction, and Termination',
    paragraphs: [
      'Jobisa may suspend, restrict, moderate, delist, or terminate accounts, profiles, jobs, campaigns, or content at its discretion where there is suspected policy breach, fraud risk, harmful conduct, legal exposure, payment abuse, security concerns, or platform misuse.',
      'Where commercially and legally appropriate, Jobisa may investigate complaints, seek clarification, request additional documents, or allow corrective action before taking permanent enforcement steps.',
    ],
  },
  {
    title: '12. Third-Party Services',
    paragraphs: [
      'The Platform may depend on third-party infrastructure and services for hosting, messaging, maps, AI processing, notifications, analytics, storage, or communications. Jobisa is not responsible for outages, changes, content, or independent policies of third-party services, except to the extent required by applicable law.',
    ],
  },
  {
    title: '13. No Employment Guarantee',
    paragraphs: [
      'Jobisa does not guarantee that workers will receive job offers, interviews, visas, travel placements, or employment outcomes, and does not guarantee that recruiters will receive suitable candidates, accepted offers, or successful hires. Jobisa acts as a platform and workflow enabler, not as a guarantor of outcomes between users.',
    ],
  },
  {
    title: '14. Disclaimers',
    paragraphs: [
      'The Platform is provided on an "as is" and "as available" basis, subject to maintenance, updates, outages, and technological limitations. To the fullest extent permitted by law, Jobisa disclaims warranties of uninterrupted availability, fitness for a particular purpose, non-infringement, and complete accuracy of user-generated or third-party content.',
    ],
  },
  {
    title: '15. Limitation of Liability',
    paragraphs: [
      'To the fullest extent permitted by applicable law, Jobisa and its affiliates, founders, employees, and service providers shall not be liable for any indirect, incidental, special, consequential, exemplary, reputational, or loss-of-profit damages arising out of or in connection with your use of the Platform, recruitment outcomes, third-party conduct, or reliance on user-generated content.',
      'Nothing in these Terms limits liability that cannot lawfully be excluded under applicable law.',
    ],
  },
  {
    title: '16. Indemnity',
    paragraphs: [
      'You agree to indemnify and hold harmless Jobisa, its affiliates, personnel, and service providers from claims, losses, liabilities, damages, and reasonable costs arising from your content, your use of the Platform, your breach of these Terms, your violation of law, or your infringement of any third-party rights.',
    ],
  },
  {
    title: '17. Governing Law and Jurisdiction',
    paragraphs: [
      'These Terms shall be governed by and construed in accordance with the laws of India. Subject to applicable law, courts of competent jurisdiction in India shall have exclusive jurisdiction over disputes arising out of or relating to these Terms or the Platform.',
    ],
  },
  {
    title: '18. Grievances and Contact',
    paragraphs: [
      'If you have questions, complaints, or grievances relating to the Platform, please contact Jobisa at hiring@jobisa.in. Jobisa may require sufficient details to verify your account and understand the issue before taking action.',
    ],
  },
  {
    title: '19. Changes to These Terms',
    paragraphs: [
      'Jobisa may update these Terms from time to time. Updated Terms will be posted on the website and may also be presented in-app where appropriate. Continued use of the Platform after the effective date of revised Terms will constitute acceptance of the updated Terms, to the extent permitted by law.',
    ],
  },
];

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 font-sans py-12">
      <Seo
        title="Terms and Conditions"
        description="Jobisa Terms and Conditions governing worker and recruiter apps, website access, messaging, WhatsApp onboarding, AI-assisted features, uploads, and recruiter billing workflows."
        path="/terms-and-conditions"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Jobisa Terms and Conditions',
          url: 'https://jobisa.in/terms-and-conditions',
          description: 'Terms and Conditions governing use of Jobisa worker and recruiter services.',
        }}
      />
      <div className="container mx-auto px-6 lg:px-12 max-w-4xl bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-100">
        <a href="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 font-medium">
          <ArrowLeft size={20} className="mr-2" /> Back to Home
        </a>

        <h1 className="text-4xl font-bold font-display text-jobisa-navy mb-4">Terms and Conditions</h1>
        <p className="text-gray-500 mb-8">Last updated: March 24, 2026</p>

        <div className="space-y-8 leading-relaxed text-gray-700">
          {sections.map((section) => (
            <section key={section.title}>
              <h2 className="text-2xl font-bold font-display text-jobisa-navy mb-4">{section.title}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="mb-3">
                  {paragraph}
                </p>
              ))}
              {section.bullets && (
                <ul className="list-disc pl-6 space-y-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 40)}>{bullet}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
